import { DOCUMENT, Injectable, computed, inject, resource, signal } from '@angular/core';

import { logError } from '../helper/log.helper';
import { DmbWizardValue } from '../model/product/wizard';

/**
 * Writes DmbWizardValue entries – without the files – to PRODUCT_WIZARD_STORE.
 * On every change the entire value is written.
 * Files are written to a second store, to avoid resaving them over and over.
 */
const PRODUCT_WIZARD_STORE = 'product_wizard';
const PRODUCT_WIZARD_FILE_STORE = 'product_wizard_file';

@Injectable({ providedIn: 'root' })
export class DBService {
  private iddb = inject(DOCUMENT).defaultView?.indexedDB;
  private blocking = signal(false);

  private db = resource({
    loader: () =>
      new Promise<IDBDatabase>((res, rej) => {
        if (!this.iddb) {
          rej('IndexedDB not available');
          return;
        }

        const openDBRequest = this.iddb.open('dmb_data', 6);

        openDBRequest.onupgradeneeded = (event) =>
          this.dbSetup(openDBRequest.result, openDBRequest.transaction, event.oldVersion);
        openDBRequest.onblocked = () => {
          this.blocking.set(true);
        };
        openDBRequest.onsuccess = () => {
          res(openDBRequest.result);
          this.blocking.set(false);
        };
        openDBRequest.onerror = () => {
          logError(openDBRequest.error);
          rej(openDBRequest.error);
        };
      }),
  });

  // 'blocking' while waiting for other tabs to close
  status = computed(() => (this.blocking() ? 'blocking' : this.db.status()));
  persisted = signal(false);

  constructor() {
    inject(DOCUMENT)
      .defaultView?.navigator?.storage?.persist()
      .then((result) => {
        this.persisted.set(result);
      });
  }

  productWizardSessionList = resource({
    params: () => (this.db.hasValue() ? this.db.value() : undefined),
    loader: async ({ params: db }) => {
      const request = db.transaction(PRODUCT_WIZARD_STORE).objectStore(PRODUCT_WIZARD_STORE).getAll();
      const fileRequest = db.transaction(PRODUCT_WIZARD_FILE_STORE).objectStore(PRODUCT_WIZARD_FILE_STORE).openCursor();

      const files = new Promise<{ id: string; file: File }[]>((res, rej) => {
        const resultList: { id: string; file: File }[] = [];

        fileRequest.onsuccess = () => {
          const cursor = fileRequest.result;

          if (cursor === null) {
            res(resultList);
          } else {
            resultList.push({ id: String(cursor.key), file: cursor.value });
            cursor.continue();
          }
        };

        fileRequest.onerror = () => rej(fileRequest.error);
      });

      const sessions = new Promise<DmbWizardValue[]>((res, rej) => {
        request.onsuccess = () => res(request.result);
        request.onerror = () => rej(request.error);
      });

      const allFiles = await files;
      const sessionList = await sessions;

      sessionList.forEach((session) => {
        session.productData.coverImages.forEach((image) => {
          const found = allFiles.find((f) => f.id === image.id);
          image.file = found?.file;
        });

        session.tracklisting.forEach((track) => {
          if (!track.existing) {
            const found = allFiles.find((f) => f.id === track.id);
            track.file = found?.file;
          }
        });

        const preview = session.tracksPreview;
        if (preview) {
          const found = allFiles.find((f) => f.id === preview.id);
          if (found) {
            preview.file = found.file;
          } else {
            session.tracksPreview = undefined;
          }
        }
      });

      return sessionList;
    },
  });

  async putProductWizardSession(session: DmbWizardValue) {
    if (!this.db.hasValue()) {
      throw new Error('Attempted to put to unavailable db');
    }

    const files: { id: string; file: File }[] = [];

    let tracksPreview: Partial<DmbWizardValue['tracksPreview']> = undefined;
    if (session.tracksPreview) {
      const { file, ...rest } = session.tracksPreview;
      files.push({ id: rest.id, file });
      tracksPreview = rest;
    }

    const sessionWithoutTracks = {
      ...session,
      productData: {
        ...session.productData,
        coverImages: session.productData.coverImages.map(({ file, ...rest }) => {
          if (file) {
            files.push({ id: rest.id, file });
          }
          return rest;
        }),
      },
      tracklisting: session.tracklisting.map((track) => {
        if (track.existing) {
          return track;
        }

        const { file, ...rest } = track;

        if (file) {
          files.push({ id: rest.id, file });
        }

        return rest;
      }),
      tracksPreview,
    };

    const dbValue = this.db.value();

    const putRequest = dbValue
      .transaction(PRODUCT_WIZARD_STORE, 'readwrite')
      .objectStore(PRODUCT_WIZARD_STORE)
      .put(sessionWithoutTracks);
    const putPromise = new Promise<void>((res, rej) => {
      putRequest.onerror = () => rej(putRequest.error);
      putRequest.onsuccess = () => res();
    });

    const keysRequest = dbValue
      .transaction(PRODUCT_WIZARD_FILE_STORE, 'readonly')
      .objectStore(PRODUCT_WIZARD_FILE_STORE)
      .getAllKeys();
    const keysPromise = new Promise<IDBValidKey[]>((res, rej) => {
      keysRequest.onerror = () => rej(keysRequest.error);
      keysRequest.onsuccess = () => res(keysRequest.result);
    });

    try {
      const keys = await keysPromise;
      const promises: Promise<unknown>[] = [];

      const fileStore = dbValue
        .transaction(PRODUCT_WIZARD_FILE_STORE, 'readwrite')
        .objectStore(PRODUCT_WIZARD_FILE_STORE);

      // Cannot be foreach, to avoid auto commit
      for (let i = 0; i < files.length; i++) {
        if (!keys.includes(files[i].id)) {
          const addRequest = fileStore.add(files[i].file, files[i].id);

          promises.push(
            new Promise<void>((res, rej) => {
              addRequest.onsuccess = () => res();
              addRequest.onerror = () => rej(addRequest.error);
            }),
          );
        }
      }

      await Promise.all(promises);
      await putPromise;

      console.assert(this.productWizardSessionList.reload(), 'Successful productWizardList reload');
    } catch (err) {
      const wrappedErr = new Error('Wizard Store add failed', { cause: err });
      logError(wrappedErr);
    }
  }

  loadProductWizardSession(id: string) {
    if (!this.productWizardSessionList.hasValue()) {
      throw new Error('Attempted to get from unavailable db');
    }

    const result = this.productWizardSessionList.value().find((session) => session.id === id);

    if (!result) {
      throw new Error('Given id is not in the list');
    }

    return result;
  }

  deleteProductWizardSession(id: string, fileIds: string[]) {
    if (!this.db.hasValue()) {
      throw new Error('Attempted to delete from unavailable db');
    }

    const dbValue = this.db.value();

    const fileStore = dbValue
      .transaction(PRODUCT_WIZARD_FILE_STORE, 'readwrite')
      .objectStore(PRODUCT_WIZARD_FILE_STORE);

    // Cannot be foreach, to avoid auto commit
    for (let i = 0; i < fileIds.length; i++) {
      fileStore.delete(fileIds[i]);
    }

    const deleteRequest = dbValue
      .transaction(PRODUCT_WIZARD_STORE, 'readwrite')
      .objectStore(PRODUCT_WIZARD_STORE)
      .delete(id);

    return new Promise<void>((res, rej) => {
      deleteRequest.onsuccess = () => {
        res();
        console.assert(this.productWizardSessionList.reload(), 'Successful productWizardList reload');
      };
      deleteRequest.onerror = () => {
        rej();
        // Maybe session was already deleted. Reload to make sure we don't have any stale sessions
        console.assert(this.productWizardSessionList.reload(), 'Successful productWizardList reload');
      };
    });
  }

  removeFile(id: string) {
    if (!this.db.hasValue()) {
      throw new Error('Attempted to delete from unavailable db');
    }

    this.db
      .value()
      .transaction(PRODUCT_WIZARD_FILE_STORE, 'readwrite')
      .objectStore(PRODUCT_WIZARD_FILE_STORE)
      .delete(id);
  }

  private dbSetup(db: IDBDatabase, transaction: IDBTransaction | null, version: number) {
    try {
      if (version < 1) {
        db.createObjectStore(PRODUCT_WIZARD_STORE, { keyPath: 'id' });
      }

      if (version < 2 && transaction) {
        const cursorRequest = transaction.objectStore(PRODUCT_WIZARD_STORE).openCursor();

        cursorRequest.onerror = () => {
          const err = new Error('Wizard Store update error', { cause: cursorRequest.error });
          logError(err);
          transaction.abort();
        };

        cursorRequest.onsuccess = () => {
          const cursor = cursorRequest.result;

          if (cursor) {
            const oldValue = cursor.value;

            cursor.update({
              ...oldValue,
              contributors: oldValue.contributors.map((c: object) => ({ clientId: '1', ...c })),
            });

            cursor.continue();
          }
        };
      }

      if (version < 3) {
        const fileStore = db.createObjectStore(PRODUCT_WIZARD_FILE_STORE);

        if (transaction) {
          const cursorRequest = transaction.objectStore(PRODUCT_WIZARD_STORE).openCursor();

          cursorRequest.onerror = () => {
            const err = new Error('Wizard Store update error', { cause: cursorRequest.error });
            logError(err);
            transaction.abort();
          };

          cursorRequest.onsuccess = () => {
            const cursor = cursorRequest.result;

            if (cursor) {
              const oldValue = cursor.value;

              let tracksPreview: Partial<DmbWizardValue['tracksPreview']> = undefined;
              if (oldValue.tracksPreview) {
                const { file, ...rest } = oldValue.tracksPreview;
                tracksPreview = rest;

                const addRequest = fileStore.add(file, rest.id);

                addRequest.onerror = () => {
                  const err = new Error('Wizard Store update error', { cause: addRequest.error });
                  logError(err);
                  transaction.abort();
                };
              }

              cursor.update({
                ...oldValue,
                productData: {
                  ...oldValue.productData,
                  coverImages: oldValue.productData.coverImages.map(({ file, ...rest }: { id: string; file: File }) => {
                    const addRequest = fileStore.add(file, rest.id);

                    addRequest.onerror = () => {
                      const err = new Error('Wizard Store update error', { cause: addRequest.error });
                      logError(err);
                      transaction.abort();
                    };

                    return rest;
                  }),
                },
                tracklisting: oldValue.tracklisting.map(({ file, ...rest }: { id: string; file: File }) => {
                  const addRequest = fileStore.add(file, rest.id);

                  addRequest.onerror = () => {
                    const err = new Error('Wizard Store update error', { cause: addRequest.error });
                    logError(err);
                    transaction.abort();
                  };

                  return rest;
                }),
                tracksPreview,
              });

              cursor.continue();
            }
          };
        }
      }

      if (version < 4 && transaction) {
        const cursorRequest = transaction.objectStore(PRODUCT_WIZARD_STORE).openCursor();

        cursorRequest.onerror = () => {
          const err = new Error('Wizard Store update error', { cause: cursorRequest.error });
          logError(err);
          transaction.abort();
        };

        cursorRequest.onsuccess = () => {
          const cursor = cursorRequest.result;

          if (cursor) {
            const oldValue = cursor.value;

            cursor.update({
              ...oldValue,
              productData: {
                ...oldValue.productData,
                bookletTitle: '',
                bookletCYear: '',
                bookletCText: '',
              },
            });

            cursor.continue();
          }
        };
      }

      if (version < 5 && transaction) {
        const cursorRequest = transaction.objectStore(PRODUCT_WIZARD_STORE).openCursor();

        cursorRequest.onerror = () => {
          const err = new Error('Wizard Store update error', { cause: cursorRequest.error });
          logError(err);
          transaction.abort();
        };

        cursorRequest.onsuccess = () => {
          const cursor = cursorRequest.result;

          if (cursor) {
            const oldValue = cursor.value;

            cursor.update({
              ...oldValue,
              productData: {
                ...oldValue.productData,
                digitalPrice: '',
              },
            });

            cursor.continue();
          }
        };
      }

      if (version < 6 && transaction) {
        const cursorRequest = transaction.objectStore(PRODUCT_WIZARD_STORE).openCursor();

        cursorRequest.onerror = () => {
          const err = new Error('Wizard Store update error', { cause: cursorRequest.error });
          logError(err);
          transaction.abort();
        };

        cursorRequest.onsuccess = () => {
          const cursor = cursorRequest.result;

          if (cursor) {
            const oldValue = cursor.value;

            cursor.update({
              ...oldValue,
              productContributors: oldValue.productContributors?.map((c: { id: string; roles: { id: string }[] }) => ({
                ...c,
                roles: c.roles?.map((r) => r.id),
              })),
              tracklisting: oldValue.tracklisting?.map(
                (t: { existing?: boolean; contributors: { id: string; roles: { id: string }[] }[] }) =>
                  t.existing
                    ? t
                    : {
                        ...t,
                        contributors: t.contributors?.map((c: { id: string; roles: { id: string }[] }) => ({
                          ...c,
                          roles: c.roles?.map((r) => r.id),
                        })),
                      },
              ),
            });

            cursor.continue();
          }
        };
      }

      db.onerror = (event) => {
        const target = event.target;

        let cause;
        if (target && 'error' in target) {
          cause = target.error;
        } else if ('error' in db) {
          cause = db.error;
        }

        if (cause !== undefined) {
          const err = new Error('DB Error', { cause });
          logError(err);
        }

        try {
          transaction?.abort();
        } catch (_) {
          // Can happen if transaction is already done, which we cannot check upfront.
        }
      };
    } catch (err) {
      logError(err);
      try {
        transaction?.abort();
      } catch (_) {
        // Can happen if transaction is already done, which we cannot check upfront.
      }
    }
  }
}
